/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useMemo } from 'react';
import { Grid as MaterialGrid, styled } from '@mui/material';
import Input from '../../../components/Input';
import TestIds from '../../../testIds';
import { DatePicker } from '../../../components/DateRangePicker';
import dayjs from 'dayjs';
import Typography from '../../../components/Typography';
import { PaddockSchema, SeedMix } from '../types/SeedOrderFormType';
import { Button } from '../../../components/Button';
import { useAppDispatch } from '../../../store/rootStore';
import { setSeedValue } from '../storage/seedCart.slice';
import { useSelector } from 'react-redux';
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage';
import { adjustQuantityAfterHectares } from '../utils/calculateTotal';
import _ from 'lodash';

const GridContainer = styled(MaterialGrid)(({ theme }) => ({
  gap: '16px',
  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.colors.grey200}`,
    paddingBottom: '32px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: 0,
    width: '100%',
  },
}));

const Grid = styled(MaterialGrid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const PaddockRow = ({
  index,
  remove,
  seedMix,
}: {
  index: number;
  remove: (index: number) => void;
  seedMix: SeedMix;
}) => {
  const hectaresField = seedMix.paddocks[index].hectares;
  const nameField = seedMix.paddocks[index].name;
  const deliveryDateField = seedMix.paddocks[index].deliveryDate;

  const account = useSelector(selectCurrentFarmerAccount);
  const accountNumber = account?.accountNumber;

  const dispatch = useAppDispatch();
  const validation = PaddockSchema.safeParse(seedMix.paddocks[index]);
  const hectaresError = validation.error?.formErrors?.fieldErrors?.hectares?.[0];
  const nameError = validation.error?.formErrors?.fieldErrors?.name?.[0];
  const deliveryDateError = validation.error?.formErrors?.fieldErrors?.deliveryDate?.[0];

  const onRemove = () => {
    remove(index);
  };

  const [hectaresValue, setHectaresValue] = React.useState<number | undefined>(hectaresField);

  const hectaresMemo = useMemo(() => {
    return (
      <Input
        label='Hectares'
        errorMessage={hectaresError}
        type='number'
        id='hectare-input'
        testId={TestIds.feat.seed.paddock.hectareInput}
        value={hectaresValue}
        required
        onChange={(event) => {
          const value = event.currentTarget.value;
          setHectaresValue(parseFloat(value));
        }}
        onBlur={() => {
          if (accountNumber) {
            let value = hectaresValue;
            if (!value || value < 1) {
              value = 0;
            }

            let copy = _.cloneDeep(seedMix);
            copy.paddocks[index].hectares = value;

            if (value !== 0) {
              copy = adjustQuantityAfterHectares(copy);

              dispatch(
                setSeedValue({
                  accountNumber,
                  key: ``,
                  value: copy,
                  seedMix,
                }),
              );
            } else {
              dispatch(
                setSeedValue({
                  accountNumber,
                  key: `paddocks[${index}].hectares`,
                  value: value,
                  seedMix,
                }),
              );
            }
          }
        }}
      />
    );
  }, [hectaresValue, hectaresError, seedMix]);

  const [nameValue, setNameValue] = React.useState<string>(nameField);

  const paddockMemo = useMemo(() => {
    return (
      <Input
        label='Paddock Name'
        errorMessage={nameError}
        type='text'
        required
        testId={TestIds.feat.seed.paddock.nameInput}
        value={nameValue}
        onChange={(event) => setNameValue(event.currentTarget.value)}
        onBlur={() => {
          if (accountNumber) {
            dispatch(
              setSeedValue({
                accountNumber,
                key: `paddocks[${index}].name`,
                value: nameValue,
                seedMix,
              }),
            );
          }
        }}
      />
    );
  }, [nameValue, nameError, seedMix]);

  return (
    <GridContainer container>
      <Grid item>{hectaresMemo}</Grid>
      <Grid item>{paddockMemo}</Grid>
      <Grid item>
        <>
          <DatePicker
            label='Preferred delivery date'
            testId={TestIds.feat.seed.paddock.dateInput}
            minDate={dayjs().addBusinessDays(3)}
            maxDate={dayjs().add(1, 'year').add(-3, 'day')}
            value={deliveryDateField}
            onChange={(value) => {
              if (accountNumber) {
                dispatch(
                  setSeedValue({
                    accountNumber,
                    key: `paddocks[${index}].deliveryDate`,
                    value: value,
                    seedMix,
                  }),
                );
              }
            }}
            onAccept={(value) => {
              if (accountNumber) {
                dispatch(
                  setSeedValue({
                    accountNumber,
                    key: `paddocks[${index}].deliveryDate`,
                    value: value,
                    seedMix,
                  }),
                );
              }
            }}
          />

          {deliveryDateError && (
            <Typography
              variant='body2'
              color='error700'
              mt={0.5}
              data-testid={TestIds.feat.seed.paddock.dateInputError}
            >
              {deliveryDateError}
            </Typography>
          )}
        </>
      </Grid>
      {index === 0 ? null : <Button onClick={onRemove}>Remove</Button>}
    </GridContainer>
  );
};
